import { WmChart, WmChartSlice } from "@watermarkinsights/ripple-react";
import translate from "@/i18n/translate";
import { useIntl } from "react-intl";
import { decode } from "html-entities";
interface StudentAssessments {
  awaitingAssessment: number;
  awaitingSubmission: number;
  done: number;
}

interface CommonAssessment {
  name: string;
  orgs: string[];
  studentAssessments: StudentAssessments;
  totalActivities: number;
}

interface ActivityDetails {
  name: string;
  orgsCount: number;
  uuid: string;
  commonAssessments: CommonAssessment[];
}
export interface StudentAssessmentProgressByTerm {
  name: String;
  studentAssessments: StudentAssessments;
  uuid: String;
  activityDetails: ActivityDetails;
}

export interface AssessmentProgressChartProps {
  assessmentProgress: StudentAssessmentProgressByTerm[];
  noActivityTerms: string;
}

export default function AssessmentProgressChart({
  assessmentProgress,
  noActivityTerms
}: AssessmentProgressChartProps) {
  const awaitingSubmission = useIntl().formatMessage({
    id: "app.assessmentProgressChart.awaitingSubmission"
  });
  const awaitingAssessment = useIntl().formatMessage({
    id: "app.assessmentProgressChart.awaitingAssessment"
  });
  const done = useIntl().formatMessage({
    id: "app.done"
  });
  function useStudentAwaitingSubmission(
    asstProgress: StudentAssessmentProgressByTerm
  ) {
    return useIntl().formatMessage(
      { id: "app.assessmentProgressChart.popoverStatusText" },
      { statusLabel: awaitingSubmission, labelText: asstProgress?.name }
    );
  }

  const StudentAwaitingSubmission = (
    asstProgress: StudentAssessmentProgressByTerm
  ) => useStudentAwaitingSubmission(asstProgress);

  function useTotalText(
    asstProgress: StudentAssessmentProgressByTerm,
    total: string
  ) {
    return useIntl().formatMessage(
      { id: "app.assessmentProgressChart.total" },
      { asstProgressName: asstProgress?.name, total: total }
    );
  }

  const TotalText = (
    asstProgress: StudentAssessmentProgressByTerm,
    total: string
  ) => useTotalText(asstProgress, total);

  function useStudentAssessmentDone(
    asstProgress: StudentAssessmentProgressByTerm
  ) {
    return useIntl().formatMessage(
      { id: "app.assessmentProgressChart.popoverStatusText" },
      { statusLabel: done, labelText: asstProgress?.name }
    );
  }

  const StudentAssessmentDone = (
    asstProgress: StudentAssessmentProgressByTerm
  ) => useStudentAssessmentDone(asstProgress);

  function useStudentAssessmentAwaiting(
    asstProgress: StudentAssessmentProgressByTerm
  ) {
    return useIntl().formatMessage(
      { id: "app.assessmentProgressChart.popoverStatusText" },
      { statusLabel: awaitingAssessment, labelText: asstProgress?.name }
    );
  }

  const StudentAssessmentAwaiting = (
    asstProgress: StudentAssessmentProgressByTerm
  ) => useStudentAssessmentAwaiting(asstProgress);

  const studentAssessmentByStatus = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.studentAssessmentsbyStatus"
  });
  const totalActivities = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.totalActivities"
  });
  const organizationLabel = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.organization"
  });
  const commonAssessmentVal = useIntl().formatMessage({
    id: "app.openDrillDownViewSll.commonAssessmentVal"
  });
  const getColumnName = () => {
    return [
      commonAssessmentVal,
      organizationLabel,
      totalActivities,
      studentAssessmentByStatus
    ];
  };

  const Table = (props: any) => {
    return <table className="data-table -shadow">{props.children}</table>;
  };

  const TableHeader = (props: any) => {
    return (
      <thead className="dataheader">
        <tr className="dataheaderrow">
          {props.columns.map((column: string, index: number) => {
            return (
              <th
                className="dataheadercell"
                data-th={column}
                key={`column-${index}`}
              >
                {column}
              </th>
            );
          })}
        </tr>
      </thead>
    );
  };

  const TableBody = (props: any) => {
    return <tbody className="databody">{props.children}</tbody>;
  };

  interface RenderStudentAssessmentByTermChartInterface {
    stuAssessment: StudentAssessments;
    labelText: string;
  }

  const RenderStudentAssessmentByTermChart = (
    props: RenderStudentAssessmentByTermChartInterface
  ) => {
    const awaitingSubmission = useIntl().formatMessage({
      id: "app.assessmentProgressChart.awaitingSubmission"
    });

    const awaitingAssessment = useIntl().formatMessage({
      id: "app.assessmentProgressChart.awaitingAssessment"
    });

    const done = useIntl().formatMessage({
      id: "app.done"
    });

    function usePopTextByStatus(status: string, labelText: string) {
      return useIntl().formatMessage(
        {
          id: "app.assessmentProgressChart.popoverStatusText"
        },

        { statusLabel: status, labelText: labelText }
      );
    }

    function useGetLabelNameVal(labelText: string) {
      return useIntl().formatMessage(
        {
          id: "app.assessmentProgressChart.labelNameText"
        },

        { labelName: labelText }
      );
    }

    const total = Number(
      Object.values(props.stuAssessment).reduce(
        (newVal: number, oldVal: number) => (newVal = newVal + oldVal),
        0
      )
    );

    return (
      <WmChart
        chart-type="bar4"
        value-format="amount"
        show-legend="false"
        label={useGetLabelNameVal(props.labelText)}
        print-mode={true}
      >
        <WmChartSlice
          legend={awaitingSubmission}
          amount={String(props.stuAssessment.awaitingSubmission)}
          popoverText={usePopTextByStatus(awaitingSubmission, props.labelText)}
          popover-title={props.stuAssessment.awaitingSubmission + "/" + total}
        />

        <WmChartSlice
          legend={awaitingAssessment}
          amount={String(props.stuAssessment.awaitingAssessment)}
          popoverText={usePopTextByStatus(awaitingAssessment, props.labelText)}
          popover-title={props.stuAssessment.awaitingAssessment + "/" + total}
        />

        <WmChartSlice
          legend={done}
          amount={String(props.stuAssessment.done)}
          popoverText={usePopTextByStatus(done, props.labelText)}
          popover-title={props.stuAssessment.done + "/" + total}
        />
      </WmChart>
    );
  };
  const renderSingleOrganization = (organizations: string[]) => {
    return organizations.map((name: string) => {
      return <div key={name}>{decode(name)}</div>;
    });
  };

  const hierarchyTableBody = (hierarchy: any) => {
    return (
      <div className="org-table sll-table-export">
        <Table>
          <TableHeader columns={getColumnName()} />
          <TableBody>
            {hierarchy.map((node: CommonAssessment, index: number) => {
              return (
                <tr className="datarow" key={index}>
                  <td className="datacell">{decode(node?.name)}</td>
                  <td className="datacell">
                    {renderSingleOrganization(node?.orgs)}
                  </td>
                  <td className="datacell">{node?.totalActivities}</td>
                  <td className="datacell">
                    <RenderStudentAssessmentByTermChart
                      stuAssessment={node?.studentAssessments}
                      labelText={decode(node?.name)}
                    ></RenderStudentAssessmentByTermChart>
                  </td>
                </tr>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="sll-chart-html" id="assessment-progress-chart">
      <div className="progress-term-label">
        {translate("app.assessmentProgressChart.studentAssessmentProgress")}
      </div>
      <div className="legend-wrapper">
        <div className="legend" aria-hidden="true">
          <div className="legend-item legend-item-html">
            <div className="legend-color legend1 -legend-wrap"></div>
            <div className="legend-text">
              {translate("app.assessmentProgressChart.awaitingSubmission")}
            </div>
          </div>
          <div className="legend-item legend-item-html">
            <div className="legend-color legend2 -legend-wrap"></div>
            <div className="legend-text">
              {translate("app.assessmentProgressChart.awaitingAssessment")}
            </div>
          </div>
          <div className="legend-item">
            <div className="legend-color legend3"></div>
            <div className="legend-text">{translate("app.done")}</div>
          </div>
        </div>
      </div>
      <div>
        {assessmentProgress.map(
          (asstProgress: StudentAssessmentProgressByTerm) => {
            const total = Number(
              Object.values(asstProgress.studentAssessments).reduce(
                (newVal: number, oldVal: number) => (newVal = newVal + oldVal),
                0
              )
            );

            return (
              <div
                className="sll-chart-container"
                key={String(asstProgress?.name)}
              >
                <div className="chart-space">
                  <WmChart
                    chart-type="bar4"
                    value-format="amount"
                    show-legend="false"
                    label={String(TotalText(asstProgress, String(total)))}
                    print-mode={true}
                  >
                    <WmChartSlice
                      legend={awaitingSubmission}
                      amount={String(
                        asstProgress.studentAssessments.awaitingSubmission
                      )}
                      popoverText={String(
                        StudentAwaitingSubmission(asstProgress)
                      )}
                      popover-title={
                        asstProgress.studentAssessments.awaitingSubmission +
                        "/" +
                        total
                      }
                    />
                    <WmChartSlice
                      legend={awaitingAssessment}
                      amount={String(
                        asstProgress.studentAssessments.awaitingAssessment
                      )}
                      popoverText={String(
                        StudentAssessmentAwaiting(asstProgress)
                      )}
                      popover-title={
                        asstProgress.studentAssessments.awaitingAssessment +
                        "/" +
                        total
                      }
                    />
                    <WmChartSlice
                      legend={done}
                      amount={String(asstProgress.studentAssessments.done)}
                      popoverText={String(StudentAssessmentDone(asstProgress))}
                      popover-title={
                        asstProgress.studentAssessments.done + "/" + total
                      }
                    />
                  </WmChart>
                </div>
                <div>
                  <section className="body">
                    <div className="flex-row -justified -verticallycentered">
                      <div id="organisation-details-html">
                        <div className="count">
                          {translate("app.openDrillDownViewSll.orgProgCount", {
                            orgCount: asstProgress.activityDetails.orgsCount
                          })}
                        </div>
                        <div className="count">
                          {translate(
                            "app.openDrillDownViewSll.commonAssessmentCount",
                            {
                              commonAssessmentCount:
                                asstProgress.activityDetails.commonAssessments
                                  .length
                            }
                          )}
                        </div>
                      </div>
                    </div>
                    {hierarchyTableBody(
                      asstProgress.activityDetails.commonAssessments
                    )}
                  </section>
                </div>
              </div>
            );
          }
        )}
        {noActivityTerms === "" ? (
          ""
        ) : (
          <div className="no-activity-label no-activity-label-html">
            <div className="-label">
              {translate("app.assessmentProgressChart.NoActivities", {
                noActivityTerms: noActivityTerms
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
